import { getComponentClasses } from '@99designs/common/utils/app';
import clsx from 'clsx';
import FormLabel from 'common/FormLabel';
import React from 'react';
import Box from 'libs/core-components/Box/Box';
import Stack from 'libs/core-components/Stack/Stack';
import { Inline } from 'libs/core-components/Inline/Inline';
import styled from 'styled-components';

export interface FieldTitleProps {
    label: React.ReactNode;
    htmlFor?: string;
}

export function FieldTitle({ htmlFor, label }: FieldTitleProps) {
    return (
        <label className="field__title" htmlFor={htmlFor}>
            {label}
        </label>
    );
}

export interface FieldValidationProps {
    message: React.ReactNode;
}

export function FieldValidation({ message = null }: FieldValidationProps) {
    if (!message) return null;
    return (
        <div className="field__validation">
            <p className="field__validation__message">{message}</p>
        </div>
    );
}

export interface FieldMessageProps {
    children: React.ReactNode;
}

export function FieldMessage({ children }: FieldMessageProps) {
    const className = clsx('field__message', {});
    return <div className={className}>{children}</div>;
}

export interface FieldProps {
    error?: boolean;
    inline?: boolean;
    checkable?: boolean;
    children: React.ReactNode;
}

export function Field({ error = false, inline = false, checkable = false, children }: FieldProps) {
    const variations = [];

    if (inline) {
        variations.push('inline');
    }

    if (checkable) {
        variations.push('checkable');
    }

    const className = clsx(getComponentClasses('field', [...variations, 'no-margin-bottom']));

    return (
        <div className={className} {...(error && { 'data-field-error': true })}>
            {children}
        </div>
    );
}

export interface FieldColumnsProps {
    isDisable: boolean;
    children: React.ReactNode;
}

export function FieldColumns({ isDisable, children }: FieldColumnsProps) {
    if (isDisable) {
        return <>{children}</>;
    }
    return <div className="field__columns">{children}</div>;
}

export interface ColumnFieldProps {
    inline?: boolean;
    label?: React.ReactNode;
    labelFor?: string;
    error?: React.ReactNode;
    description?: React.ReactNode;
    showDescriptionInColumn?: boolean;
    children: React.ReactNode;
}

export function ColumnField({
    inline = false,
    label,
    labelFor, // FIXME: change to htmlFor for consistency
    error = null,
    description = null, // FIXME: change to message for consistency
    showDescriptionInColumn = false,
    children,
}: ColumnFieldProps) {
    return (
        <Field inline={inline} error={Boolean(error)}>
            {label && (
                <Box marginBottom="space6">
                    <FormLabel htmlFor={labelFor}>{label}</FormLabel>
                </Box>
            )}
            {!inline && <FieldValidation message={error} />}
            <FieldColumns isDisable={!showDescriptionInColumn}>
                <Stack space="space6">
                    {children}
                    <FieldMessage>{description}</FieldMessage>
                </Stack>
            </FieldColumns>
        </Field>
    );
}

export interface RadioFieldProps {
    id: string;
    label?: React.ReactNode;
    name: string;
    value: string;
    disabled?: boolean;
    checked?: boolean;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const RadioFieldTitle = styled.label`
    font-size: 14px;
`;

// FIXME: use InputField
export function RadioField({
    id,
    label,
    name,
    value,
    disabled = false,
    checked = false,
    onChange,
}: RadioFieldProps) {
    return (
        <Field inline={true} checkable={true}>
            <Inline space="quarterGutter">
                <input
                    id={id}
                    name={name}
                    type="radio"
                    className="input input-radio input--inline"
                    value={value}
                    checked={checked}
                    disabled={disabled}
                    onChange={onChange}
                />
                {label && <RadioFieldTitle htmlFor={id}>{label}</RadioFieldTitle>}
            </Inline>
        </Field>
    );
}
